"use client";

import * as Sentry from "@sentry/nextjs";
import { useEffect, useRef } from "react";
import { phCaptureException } from "@/utils/analytics";
import { DISCORD_URL } from "@/lib/landing-constants";
import {
  isReactHookCountError,
  isReactUpdateDepthError,
  planRouteRecovery,
  INITIAL_ROUTE_RECOVER_STATE,
  type RouteRecoverState,
} from "@/lib/transient-hook-error";

let routeRecoverState: RouteRecoverState = INITIAL_ROUTE_RECOVER_STATE;

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isHookDesync = isReactHookCountError(error);
  const isUpdateDepthLoop = isReactUpdateDepthError(error);
  const recoveryPlanRef = useRef<{ recover: boolean } | null>(null);
  if (recoveryPlanRef.current === null) {
    const plan = planRouteRecovery(error, routeRecoverState, Date.now());
    routeRecoverState = plan.nextState;
    recoveryPlanRef.current = { recover: plan.recover };
  }
  const willAutoRecover = recoveryPlanRef.current.recover;
  const didResetRef = useRef(false);

  useEffect(() => {
    Sentry.captureException(error, { tags: { boundary: "route" } });
    console.error("Application error:", error);
    phCaptureException(error, {
      boundary: "route",
      digest: error.digest,
      hook_desync: isHookDesync,
      update_depth_loop: isUpdateDepthLoop,
      auto_recovered: willAutoRecover,
    });
  }, [error, isHookDesync, isUpdateDepthLoop, willAutoRecover]);

  useEffect(() => {
    if (!willAutoRecover || didResetRef.current) return;
    didResetRef.current = true;
    const id = requestAnimationFrame(() => reset());
    return () => cancelAnimationFrame(id);
  }, [willAutoRecover, reset]);

  if (willAutoRecover) {
    return <div className="min-h-screen bg-background" aria-busy="true" aria-live="polite" />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">
          Something went wrong
        </h1>
        <p className="text-muted-foreground mb-6 max-w-md">
          An unexpected error occurred. Please try again or contact support if
          the problem persists.
        </p>
        {error.digest && (
          <p className="text-xs text-muted-foreground mb-4">
            Error ID: {error.digest}
          </p>
        )}
        <div className="flex gap-4 justify-center">
          <button type="button"
            onClick={reset}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Try again
          </button>
          <a
            href="/"
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
          >
            Go home
          </a>
        </div>
        <a
          href={DISCORD_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Report this on Discord for quick help
        </a>
      </div>
    </div>
  );
}
